import { createTheme } from "@mui/material/styles";

export const theme = createTheme({
  palette: {
    primary: {
      // Official FlahaFAST mark fills (flaha.org logos/apps/flahafast.svg)
      main: "#198e72",
      light: "#1babc8",
      dark: "#057b66",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#1babc8",
      light: "#57d4e8",
      dark: "#016988",
      contrastText: "#ffffff",
    },
    error: {
      main: "#e74c3c",
      light: "#f1948a",
      dark: "#b03a2e",
    },
    warning: {
      main: "#f39c12",
      light: "#f8c471",
      dark: "#b9770e",
    },
    info: {
      main: "#0b7d9d", // Accent Blue
      light: "#15bfd5",
      dark: "#065a6f",
    },
    success: {
      main: "#107121", // Success Green
      light: "#4caf50",
      dark: "#0d5a1a",
    },
    background: {
      default: "#f8f6f3", // Background Cream
      paper: "#ffffff",
    },
    text: {
      primary: "#2c3e50",
      secondary: "#7f8c8d",
      disabled: "#bdc3c7",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2.5rem",
      fontWeight: 500,
      lineHeight: 1.25,
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 500,
      lineHeight: 1.25,
    },
    h3: {
      fontSize: "1.75rem",
      fontWeight: 500,
      lineHeight: 1.25,
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 500,
      lineHeight: 1.25,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 500,
      lineHeight: 1.25,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.25,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.5,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.5,
    },
    button: {
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    "none",
    "0 1px 3px rgba(0, 0, 0, 0.1)",
    "0 2px 4px rgba(0, 0, 0, 0.1)",
    "0 4px 6px rgba(0, 0, 0, 0.1)",
    "0 6px 8px rgba(0, 0, 0, 0.1)",
    "0 8px 10px rgba(0, 0, 0, 0.1)",
    "0 10px 12px rgba(0, 0, 0, 0.1)",
    "0 12px 14px rgba(0, 0, 0, 0.1)",
    "0 14px 16px rgba(0, 0, 0, 0.1)",
    "0 16px 18px rgba(0, 0, 0, 0.1)",
    "0 18px 20px rgba(0, 0, 0, 0.1)",
    "0 20px 22px rgba(0, 0, 0, 0.1)",
    "0 22px 24px rgba(0, 0, 0, 0.1)",
    "0 24px 26px rgba(0, 0, 0, 0.1)",
    "0 26px 28px rgba(0, 0, 0, 0.1)",
    "0 28px 30px rgba(0, 0, 0, 0.1)",
    "0 30px 32px rgba(0, 0, 0, 0.1)",
    "0 32px 34px rgba(0, 0, 0, 0.1)",
    "0 34px 36px rgba(0, 0, 0, 0.1)",
    "0 36px 38px rgba(0, 0, 0, 0.1)",
    "0 38px 40px rgba(0, 0, 0, 0.1)",
    "0 40px 42px rgba(0, 0, 0, 0.1)",
    "0 42px 44px rgba(0, 0, 0, 0.1)",
    "0 44px 46px rgba(0, 0, 0, 0.1)",
    "0 46px 48px rgba(0, 0, 0, 0.1)",
  ],
  spacing: (factor: number) => `${0.25 * factor}rem`,
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: "0.45rem 0.9rem",
          textTransform: "none",
          fontWeight: 600,
        },
      },
      variants: [
        {
          props: { variant: "text" },
          style: {
            "&:hover": {
              backgroundColor: "rgba(5, 124, 105, 0.08)",
            },
          },
        },
        {
          props: { variant: "outlined" },
          style: {
            "&:hover": {
              backgroundColor: "rgba(5, 124, 105, 0.06)",
            },
          },
        },
      ],
    },
    MuiCard: {
      defaultProps: {
        variant: "outlined",
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderRadius: 6,
          borderColor: "rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        outlined: {
          borderColor: "rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 6,
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
    },
  },
});

export default theme;
